import styled, { keyframes } from "styled-components";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'};
  border: 1px solid ${({ theme }) => theme.border || 'rgba(255,255,255,0.06)'};
  border-radius: 12px;

  .global-progress {
    display: flex;
    flex-direction: column;
    gap: 6px;

    .progress-text {
      font-size: 0.8em;
      color: ${({ theme }) => theme.text || '#fff'};
      font-weight: 500;
      opacity: 0.7;

      span { color: #10b981; font-weight: 700; }
      small { opacity: 0.5; margin-left: 4px; }
    }

    .progress-bar-container {
      height: 5px;
      background: ${({ theme }) => theme.border || 'rgba(255,255,255,0.08)'};
      border-radius: 3px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        background: linear-gradient(90deg, #10b981, #34d399);
        border-radius: 3px;
        transition: width 0.5s ease;
      }
    }
  }
`;

export const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TaskItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.015)'};
  border: 1px solid transparent;
  transition: all 0.3s ease;

  &.active {
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(16,185,129,0.04)' : 'rgba(16,185,129,0.05)'};
    border-color: rgba(16, 185, 129, 0.2);
  }

  &.completed {
    opacity: 0.7;
  }

  &.error {
    background: rgba(239, 68, 68, 0.05);
    border-color: rgba(239, 68, 68, 0.2);
  }

  .task-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    font-size: 0.85em;
    flex-shrink: 0;

    &.pending {
      color: ${({ theme }) => theme.text || '#fff'};
      opacity: 0.3;
    }
    &.active {
      color: #10b981;
      svg.spin { animation: ${spin} 1s linear infinite; }
    }
    &.completed { color: #10b981; }
    &.error { color: #ef4444; }
  }

  .task-info {
    flex: 1;
    min-width: 0;

    .task-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .task-name {
        font-size: 0.82em;
        font-weight: 500;
        color: ${({ theme }) => theme.text || '#fff'};
        opacity: 0.85;
      }

      .task-status {
        font-size: 0.7em;
        font-weight: 500;
        opacity: 0.5;
        color: ${({ theme }) => theme.text || '#fff'};

        &.active { color: #10b981; opacity: 1; }
        &.completed { color: #10b981; }
        &.error { color: #ef4444; opacity: 1; }
      }
    }

    .task-progress {
      margin-top: 4px;
      height: 3px;
      background: ${({ theme }) => theme.border || 'rgba(255,255,255,0.06)'};
      border-radius: 2px;
      overflow: hidden;

      .progress-fill {
        height: 100%;
        border-radius: 2px;
        transition: width 0.3s ease;

        &.active {
          background: linear-gradient(90deg, #10b981, #34d399);
          animation: ${keyframes`
            0% { transform: translateX(-60%); }
            100% { transform: translateX(100%); }
          `} 1.5s ease-in-out infinite;
          width: 60% !important;
        }

        &.completed {
          background: rgba(16, 185, 129, 0.3);
        }
      }
    }
  }
`;

export const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  margin-top: 8px;

  &.success {
    background: rgba(16, 185, 129, 0.08);
    border: 1px solid rgba(16, 185, 129, 0.2);
  }

  &.error {
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.2);
  }

  .status-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    flex-shrink: 0;

    &.success {
      background: rgba(16, 185, 129, 0.15);
      color: #10b981;
    }
    &.error {
      background: rgba(239, 68, 68, 0.15);
      color: #ef4444;
    }
  }

  .status-text {
    flex: 1;
    h4 {
      margin: 0;
      font-size: 0.85em;
      font-weight: 600;
      color: ${({ theme }) => theme.text || '#fff'};
    }
    p {
      margin: 2px 0 0;
      font-size: 0.75em;
      color: ${({ theme }) => theme.text || '#fff'};
      opacity: 0.6;
    }
  }

  .status-action {
    button {
      padding: 6px 14px;
      border-radius: 8px;
      border: 1px solid rgba(16, 185, 129, 0.3);
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
      font-size: 0.78em;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: rgba(16, 185, 129, 0.2);
      }
    }
  }
`;
